import { firstNumeric, firstText, getRepairQuota } from "../../utils/reportingRows";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { measures } from "../../data/measures";
import { useAccountSettings } from "../../hooks/useAccountSettings";
import { useEmissionsData } from "../../hooks/useEmissionsData";
import { useModalFocusTrap } from "../../hooks/useModalFocusTrap";
import {
  showProcessingErrorNotification,
  showProcessingNotification,
  showProcessingSuccessNotification,
} from "../../utils/notifications";

import AccountSettingsPanel from "../dashboard/AccountSettingsPanel";
import DashboardSkeleton from "../dashboard/DashboardSkeleton";

import { exportToPDF } from "./exportUtils";
import PricingModal from "./PricingModal";
import BalanceSection from "./sections/BalanceSection";
import CO2IntensityEnergySection from "./sections/CO2IntensityEnergySection";
import DataQualityBadge from "./sections/DataQualityBadge";
import HeroSection from "./sections/HeroSection";
import NetZeroProgressSection from "./sections/NetZeroProgressSection";
import PathwaySection from "./sections/PathwaySection";
import {
  ACCENT,
  BRAND,
  INTERACTION,
  MUTED,
  PRIMARY,
} from "./shared/styles";
import YearDropdown from "./shared/YearDropdown";
import WelcomeBanner from "./WelcomeBanner";

const DATENERHEBUNG_SAVED_KEY = "datenerhebung_saved";
// Temporarily export only the overview without opening the configurator.
const OVERVIEW_EXPORT_CONFIG = {
  sections: {
    summary: true,
    emissions: true,
    pathway: false,
    measures: false,
    scoring: false,
    netZero: false,
  },
  scopeGroup: "all",
  includeTimestamp: true,
};

function aggregateByScope(rows) {
  return (rows || []).reduce((accumulator, row) => {
    if (
      !row?.scope ||
      row.total_emissions == null
    ) {
      return accumulator;
    }

    // Only sum top-level scope rows ("Scope 1" / "Scope 2" / "Scope 3"). Sub-scope rows
    // ("Scope 1.1", "Scope 1.2", ...) are already included in their parent's total —
    // adding them again here would double-count emissions.
    if (row.scope.includes(".")) {
      return accumulator;
    }

    accumulator[row.scope] =
      (accumulator[row.scope] || 0) +
      Number(row.total_emissions || 0);

    return accumulator;
  }, {});
}

function scoreMeasure(measure) {
  const annualSavings =
    measure.einsparung_pro_jahr || 0;

  const relativeSavings =
    measure.relative_kosteneinsparung || 0;

  const investment =
    measure.investitionsbedarf || 0;

  const paybackYears =
    measure.amortisationszeit || 0;

  const repairBoost =
    measure.id === "m4" ? 18 : 0;

  const paybackBoost =
    paybackYears > 0
      ? Math.max(
        0,
        12 - paybackYears
      ) * 2
      : 0;

  const investmentPenalty =
    investment / 15000;

  return (
    annualSavings * 2 +
    relativeSavings * 0.35 +
    repairBoost +
    paybackBoost -
    investmentPenalty
  );
}

function buildDefaultPlan() {
  const currentYear =
    new Date().getFullYear();

  return [...measures]
    .sort(
      (a, b) =>
        scoreMeasure(b) -
        scoreMeasure(a)
    )
    .slice(0, 3)
    .map((measure) => ({
      ...measure,
      startYear: currentYear,
    }));
}

const NET_ZERO_TARGET_YEAR = 2050;

function buildNetZeroProgress(
  yearlyTotals,
  selectedYear
) {
  if (
    selectedYear == null ||
    !Array.isArray(yearlyTotals)
  ) {
    return null;
  }

  const sortedTotals = yearlyTotals
    .filter(
      (row) =>
        Number.isFinite(row?.year) &&
        Number.isFinite(row?.actual) &&
        row.actual > 0
    )
    .sort(
      (a, b) =>
        a.year - b.year
    );

  const base = sortedTotals[0];
  const current =
    sortedTotals.find(
      (row) =>
        row.year === selectedYear
    ) || null;

  if (!base || !current) {
    return null;
  }

  const progressPct =
    Math.min(
      100,
      Math.max(
        0,
        ((base.actual - current.actual) /
          base.actual) *
        100
      )
    );

  const targetWindow =
    NET_ZERO_TARGET_YEAR - base.year;

  const thresholdPct =
    targetWindow > 0
      ? Math.max(
        0,
        Math.min(
          100,
          ((current.year - base.year) /
            targetWindow) *
          100
        )
      )
      : 100;

  const isBaseYear =
    current.year === base.year;

  return {
    progressPct,
    thresholdPct,
    scaleMaxPct: 100,
    year: current.year,
    onTrack: isBaseYear
      ? null
      : progressPct >= thresholdPct,
    notice: isBaseYear
      ? "Dieses Reportingjahr ist das Basisjahr. Ab dem nächsten Jahr wird sichtbar, ob der Betrieb auf Kurs ist."
      : null,
  };
}

export default function GreenOneDashboardV2({
  user,
  onLogout,
  onEingabemaskeClick,
}) {
  const {
    scopes,
    companyName: apiCompanyName,
    scoringByYear,
    isLoading,
    isAwaitingUpdate,
    error,
    retry,
  } = useEmissionsData(60, {});

  const [
    selectedYear,
    setSelectedYear,
  ] = useState(null);

  const [
    selectedMeasures,
    setSelectedMeasures,
  ] = useState([]);

  const [
    exportMeasures,
    setExportMeasures,
  ] = useState(selectedMeasures);

  const [
    generatedAt,
    setGeneratedAt,
  ] = useState(null);

  const [
    windowWidth,
    setWindowWidth,
  ] = useState(
    () => window.innerWidth
  );

  const [
    awaitingSaveRefresh,
    setAwaitingSaveRefresh,
  ] = useState(false);

  useEffect(() => {
    if (
      sessionStorage.getItem(
        DATENERHEBUNG_SAVED_KEY
      )
    ) {
      setAwaitingSaveRefresh(
        true
      );

      showProcessingNotification({
        message:
          "Daten werden verarbeitet und das Dashboard wird aktualisiert.",
      });
    }
  }, []);

  const [
    showFeedbackModal,
    setShowFeedbackModal,
  ] = useState(false);

  const [
    showPricingModal,
    setShowPricingModal,
  ] = useState(false);

  const [
    feedbackError,
    setFeedbackError,
  ] = useState("");

  const closeFeedbackModal = useCallback(() => {
    setShowFeedbackModal(false);
    setFeedbackError("");
  }, []);

  const feedbackModalRef =
    useModalFocusTrap(showFeedbackModal, closeFeedbackModal);

  const feedbackInputRef =
    useRef(null);

  const feedbackSelectRef =
    useRef(null);

  const {
    accountSettings,
    setAccountSettings,
    accountSaveState,
    setAccountSaveState,
    showAccountPanel,
    setShowAccountPanel,
    handleSaveAccountSettings,
  } = useAccountSettings(user);

  useEffect(() => {
    const dashboard =
      document.querySelector(
        ".greenone-dashboard-v2"
      );

    if (!dashboard) {
      return undefined;
    }

    function styleSdxButton(button) {
      const shadowRoot =
        button.shadowRoot;

      if (!shadowRoot) {
        return;
      }

      if (
        shadowRoot.querySelector(
          "[data-greenone-button-styles]"
        )
      ) {
        return;
      }

      const style =
        document.createElement(
          "style"
        );

      style.setAttribute(
        "data-greenone-button-styles",
        "true"
      );

      style.textContent = `
      button.primary {
        background: ${PRIMARY} !important;
        border-color: ${PRIMARY} !important;
        color: #fff !important;
      }

      button.primary:hover {
        background: ${INTERACTION} !important;
        border-color: ${INTERACTION} !important;
        color: #fff !important;
      }

      button.secondary {
        background: #fff !important;
        border-color: ${INTERACTION} !important;
        color: ${INTERACTION} !important;
      }

      button.secondary:hover {
        background: ${INTERACTION} !important;
        border-color: ${INTERACTION} !important;
        color: #fff !important;
      }

      button.primary:focus-visible,
      button.secondary:focus-visible {
        outline: 3px solid ${ACCENT} !important;
        outline-offset: 2px !important;
      }
    `;

      shadowRoot.appendChild(
        style
      );
    }

    function styleSdxInput(input) {
      const shadowRoot =
        input.shadowRoot;

      if (!shadowRoot) {
        return;
      }

      if (
        shadowRoot.querySelector(
          "[data-greenone-input-styles]"
        )
      ) {
        return;
      }

      const style =
        document.createElement(
          "style"
        );

      style.setAttribute(
        "data-greenone-input-styles",
        "true"
      );

      style.textContent = `
      input:focus,
      input:focus-visible,
      textarea:focus,
      textarea:focus-visible,
      .input:focus,
      .input:focus-visible,
      :host(:focus-within) .input,
      :host(:focus-within) input,
      :host(:focus-within) textarea {
        border-color: ${INTERACTION} !important;
        outline-color: ${INTERACTION} !important;
        box-shadow: 0 0 0 1px ${INTERACTION} !important;
      }
    `;

      shadowRoot.appendChild(
        style
      );
    }

    function styleSdxSelect(select) {
      const shadowRoot =
        select.shadowRoot;

      if (!shadowRoot) {
        return;
      }

      if (
        !shadowRoot.querySelector(
          "[data-greenone-select-styles]"
        )
      ) {
        const style =
          document.createElement(
            "style"
          );

        style.setAttribute(
          "data-greenone-select-styles",
          "true"
        );

        style.textContent = `
        .thumb {
          color: ${INTERACTION} !important;
          border-color: ${INTERACTION} !important;
        }

        :host(:focus-within) .thumb {
          color: ${INTERACTION} !important;
          border-color: ${INTERACTION} !important;
        }
      `;

        shadowRoot.appendChild(
          style
        );
      }

      shadowRoot
        .querySelectorAll(
          "sdx-input"
        )
        .forEach(
          styleSdxInput
        );
    }

    function applyComponentStyles() {
      dashboard
        .querySelectorAll(
          "sdx-button"
        )
        .forEach(
          styleSdxButton
        );

      dashboard
        .querySelectorAll(
          "sdx-input"
        )
        .forEach(
          styleSdxInput
        );

      dashboard
        .querySelectorAll(
          "sdx-select"
        )
        .forEach(
          styleSdxSelect
        );
    }

    applyComponentStyles();

    const observer =
      new MutationObserver(
        applyComponentStyles
      );

    observer.observe(
      dashboard,
      {
        childList: true,
        subtree: true,
      }
    );

    return () => {
      observer.disconnect();
    };

  }, [isLoading]);

  useEffect(() => {
    function handleResize() {
      setWindowWidth(
        window.innerWidth
      );
    }

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);

  const safeScopes = useMemo(
    () =>
      Array.isArray(scopes)
        ? scopes
        : [],
    [scopes]
  );

  const hasNoData =
    safeScopes.length === 0;

  useEffect(() => {
    if (hasNoData) {
      setSelectedMeasures([]);
      return;
    }

    setSelectedMeasures((previousMeasures) =>
      previousMeasures.length > 0
        ? previousMeasures
        : buildDefaultPlan()
    );
  }, [hasNoData]);

  const [
    welcomeDismissed,
    setWelcomeDismissed,
  ] = useState(false);

  const byYear = useMemo(() => {
    return safeScopes.reduce(
      (accumulator, row) => {
        if (!row?.year) {
          return accumulator;
        }

        if (
          !accumulator[row.year]
        ) {
          accumulator[row.year] =
            [];
        }

        accumulator[row.year].push(
          row
        );

        return accumulator;
      },
      {}
    );
  }, [safeScopes]);

  const years = useMemo(
    () =>
      Object.keys(byYear)
        .map(Number)
        .sort(
          (a, b) => b - a
        ),
    [byYear]
  );

  useEffect(() => {
    if (
      years.length > 0 &&
      (selectedYear == null ||
        !years.includes(
          selectedYear
        ))
    ) {
      setSelectedYear(
        years[0]
      );
    }
  }, [years, selectedYear]);

  useEffect(() => {
    if (
      !isLoading &&
      !error &&
      safeScopes.length > 0
    ) {
      setGeneratedAt(
        new Date()
      );
    }
  }, [
    isLoading,
    error,
    safeScopes.length,
  ]);

  useEffect(() => {
    if (
      !awaitingSaveRefresh ||
      isLoading
    ) {
      return;
    }

    setAwaitingSaveRefresh(
      false
    );

    if (error) {
      showProcessingErrorNotification({
        message:
          "Die Eingaben wurden gespeichert, aber die neuen Dashboard-Daten konnten noch nicht geladen werden.",
      });
    } else {
      showProcessingSuccessNotification({
        message:
          "Dashboard wurde aktualisiert.",
      });
    }
  }, [
    awaitingSaveRefresh,
    isLoading,
    error,
  ]);

  const yearRows =
    byYear[selectedYear] || [];

  const scopeTotals =
    aggregateByScope(yearRows);

  const yearlyTotals = years
    .map((year) => ({
      year,
      actual: Object.values(
        aggregateByScope(
          byYear[year]
        )
      ).reduce(
        (total, value) =>
          total + value,
        0
      ),
    }))
    .filter(
      (row) => row.actual > 0
    )
    .sort(
      (a, b) =>
        a.year - b.year
    );

  const employees =
    firstNumeric(yearRows, [
      "fte",
      "mitarbeitende",
      "produktive_mitarbeitende",
      "productive_fte",
    ]) ??
    firstNumeric(safeScopes, [
      "fte",
      "mitarbeitende",
      "produktive_mitarbeitende",
      "productive_fte",
    ]);

  const operationType =
    firstText(yearRows, [
      "betriebstyp",
      "business_type",
      "operation_type",
      "betrieb_typ",
    ]) ??
    firstText(safeScopes, [
      "betriebstyp",
      "business_type",
      "operation_type",
      "betrieb_typ",
    ]);

  // Raw passthrough fields from reporting_data — no calculation, just read as-is.
  const fleetElectrificationPct =
    firstNumeric(yearRows, [
      "fleet_electr_pct",
      "flotten_elektrifizierung_pct",
    ]) ??
    firstNumeric(safeScopes, [
      "fleet_electr_pct",
      "flotten_elektrifizierung_pct",
    ]);

  const waterBasePaintPct =
    firstNumeric(yearRows, [
      "waterbased_paint_pct",
      "wasserbasislack_anteil_pct",
    ]) ??
    firstNumeric(safeScopes, [
      "waterbased_paint_pct",
      "wasserbasislack_anteil_pct",
    ]);

  const repairQuota = getRepairQuota(safeScopes, selectedYear);

  // E3 Scoring/Benchmarking: CO2-Intensitaet, Massnahmenwirkung und
  // Datenqualitaets-Score werden aus dem Backend gelesen. Der Netto-Null-Kurs
  // wird aus den vorhandenen Jahres-Emissionen berechnet.
  const scoring =
    selectedYear == null
      ? null
      : scoringByYear?.[selectedYear] ?? null;

  const dataQualityScore = scoring?.dataQuality?.score ?? null;
  const co2Intensity = scoring?.co2Intensity ?? null;
  const netZero = buildNetZeroProgress(
    yearlyTotals,
    selectedYear
  );
  const scoringMeasures = scoring?.measures ?? [];
  const co2IntensityOverall = co2Intensity?.overall?.betrieb ?? null;

  const prevYear =
    years[
    years.indexOf(selectedYear) + 1
    ];

  const prevYearRows =
    byYear[prevYear] || [];

  const companyName =
    apiCompanyName ||
    user?.attributes?.[
    "custom:company"
    ] ||
    "";

  const exportDataset = {
    companyName,
    currentYear: selectedYear,
    previousYear: prevYear,
    currentYearRows: yearRows,
    prevYearRows,
    years,
    byYear,
    yearlyTotals,
    scopeTotals,
    employees,
    operationType,
    co2Intensity,
    co2IntensityOverall,
    dataQualityScore,
    scoringMeasures,
    fleetElectrificationPct,
    waterBasePaintPct,
    repairQuota,
    netZero,
    selectedMeasures: exportMeasures,
    generatedAt,
  };

  const accountEmail =
    accountSettings?.profile
      ?.email ||
    user?.attributes?.email ||
    "";

  const recommendedMeasures =
    useMemo(
      () =>
        [...measures].sort(
          (a, b) =>
            scoreMeasure(b) -
            scoreMeasure(a)
        ),
      []
    );

  function handleToggleMeasure(
    measure,
    checked
  ) {
    const currentYear =
      new Date().getFullYear();

    setSelectedMeasures(
      (previousMeasures) => {
        if (checked) {
          const alreadySelected =
            previousMeasures.some(
              (
                selectedMeasure
              ) =>
                selectedMeasure.id ===
                measure.id
            );

          if (
            alreadySelected
          ) {
            return previousMeasures;
          }

          return [
            ...previousMeasures,
            {
              ...measure,
              startYear:
                currentYear,
            },
          ];
        }

        return previousMeasures.filter(
          (
            selectedMeasure
          ) =>
            selectedMeasure.id !==
            measure.id
        );
      }
    );
  }

  function handleChangeStartYear(
    measureId,
    year
  ) {
    if (
      Number.isNaN(year)
    ) {
      return;
    }

    setSelectedMeasures(
      (previousMeasures) =>
        previousMeasures.map(
          (measure) =>
            measure.id ===
              measureId
              ? {
                ...measure,
                startYear:
                  year,
              }
              : measure
        )
    );
  }

  function handleSendFeedback() {
    const feedbackText =
      feedbackInputRef.current
        ?.value ?? "";

    const feedbackType =
      feedbackSelectRef.current
        ?.value ??
      "Bug melden";

    if (
      !feedbackText.trim()
    ) {
      setFeedbackError(
        "Bitte gib zuerst dein Feedback ein."
      );

      return;
    }

    const userEmail =
      accountSettings?.profile
        ?.email ||
      user?.attributes?.email ||
      "";

    const company =
      user?.attributes?.[
      "custom:company"
      ] || "";

    const subject =
      `GreenOne Feedback: ${feedbackType}`;

    const body =
      `Feedback-Typ:\n${feedbackType}` +
      `\n\nFeedback:\n${feedbackText}` +
      `\n\nUser-E-Mail:\n${userEmail}` +
      `\n\nFirma:\n${company}`;

    const mailtoLink =
      "mailto:islem.mdimagh@swisscom.com" +
      `?subject=${encodeURIComponent(
        subject
      )}` +
      `&body=${encodeURIComponent(
        body
      )}`;

    setShowFeedbackModal(
      false
    );

    setFeedbackError("");

    window.location.href =
      mailtoLink;
  }

  if (error && (isAwaitingUpdate || safeScopes.length === 0)) {
    return (
      <div
        style={{
          maxWidth: 900,
          margin: "3rem auto",
          padding: "1.25rem",
          border:
            "1px solid #fecaca",
          borderRadius: 8,
          background: "#fef2f2",
          color: "#991b1b",
        }}
      >
        <p role="alert">{error}</p>
        <sdx-button theme="secondary" label="Erneut versuchen" onClick={retry}></sdx-button>
      </div>
    );
  }

  if (isLoading || isAwaitingUpdate) {
    return (
      <DashboardSkeleton />
    );
  }

  const isMobile =
    windowWidth < 700;

  const isTablet =
    windowWidth < 1100;

  const isCompactFeedback =
    windowWidth < 1600;

  return (
    <main
      className="greenone-dashboard-v2"
      style={{
        padding: isMobile
          ? "1rem"
          : isTablet
            ? "1.5rem"
            : "2rem",
        width: "100%",
        maxWidth: 1440,
        margin: "0 auto",
        boxSizing:
          "border-box",
        display: "flex",
        flexDirection:
          "column",
        gap: "1rem",
      }}
    >
      <AccountSettingsPanel
        isOpen={
          showAccountPanel
        }
        user={user}
        settings={
          accountSettings
        }
        saveState={
          accountSaveState
        }
        onClose={() =>
          setShowAccountPanel(
            false
          )
        }
        onSettingsChange={(
          nextSettings
        ) => {
          setAccountSaveState(
            "idle"
          );

          setAccountSettings(
            nextSettings
          );
        }}
        onSave={(
          nextSettings
        ) =>
          handleSaveAccountSettings(
            nextSettings
          )
        }
        onLogout={onLogout}
      />

      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />

      {showFeedbackModal && (
        <div
          onClick={closeFeedbackModal}
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15, 23, 42, 0.45)",
            zIndex: 99999,
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            padding: "1rem",
          }}
        >
          <div
            ref={feedbackModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-modal-title"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 560,
              maxHeight:
                "90vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 14,
              boxShadow:
                "0 24px 60px rgba(15, 23, 42, 0.25)",
              padding: isMobile
                ? "1rem"
                : "1.5rem",
              boxSizing:
                "border-box",
            }}
          >
            <h2
              id="feedback-modal-title"
              style={{
                marginTop: 0,
                marginBottom: "0.75rem",
                color: BRAND,
              }}
            >
              Feedback senden
            </h2>

            <p
              style={{
                marginTop: 0,
                color: MUTED,
                fontSize:
                  "0.95rem",
              }}
            >
              Teile uns Bugs,
              Fragen oder
              Verbesserungsvorschläge
              direkt per E-Mail mit.
            </p>

            <div
              style={{
                marginBottom:
                  "1rem",
              }}
            >
              <sdx-select
                ref={
                  feedbackSelectRef
                }
                label="Feedback-Typ"
                placeholder="Wähle einen Feedback-Typ"
              >
                <sdx-select-option value="Bug melden">
                  Bug melden
                </sdx-select-option>

                <sdx-select-option value="Verbesserungsvorschlag">
                  Verbesserungsvorschlag
                </sdx-select-option>

                <sdx-select-option value="Frage / Unklarheit">
                  Frage / Unklarheit
                </sdx-select-option>

                <sdx-select-option value="Allgemeines Feedback">
                  Allgemeines Feedback
                </sdx-select-option>
              </sdx-select>
            </div>

            <div
              style={{
                marginBottom:
                  feedbackError
                    ? "0.5rem"
                    : "1rem",
              }}
            >
              <sdx-input
                ref={
                  feedbackInputRef
                }
                type="textarea"
                label="Feedback"
                placeholder="Beschreibe dein Feedback..."
                maxlength="1000"
              ></sdx-input>
            </div>

            {feedbackError && (
              <div
                style={{
                  marginBottom:
                    "1rem",
                  color:
                    "#dc2626",
                  fontSize:
                    "0.9rem",
                  fontWeight: 600,
                }}
              >
                {feedbackError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
                gap: "0.75rem",
                marginTop:
                  "1.25rem",
                flexWrap: "wrap",
              }}
            >
              <sdx-button
                theme="cancel"
                label="Abbrechen"
                icon="icon-clear-circle"
                onClick={closeFeedbackModal}
              />

              <sdx-button
                theme="primary"
                label="Feedback absenden"
                onClick={
                  handleSendFeedback
                }
              ></sdx-button>
            </div>
          </div>
        </div>
      )}

      {hasNoData &&
        !welcomeDismissed && (
          <WelcomeBanner
            onClose={() =>
              setWelcomeDismissed(
                true
              )
            }
          />
        )}

      <HeroSection
        companyName={
          companyName
        }
        betriebstyp={
          operationType
        }
        fte={employees}
        standort={accountSettings?.profile?.location || "Standort folgt"}
        generatedAt={
          generatedAt
        }
        onAddDataClick={() => {
          if (
            onEingabemaskeClick
          ) {
            onEingabemaskeClick();
            return;
          }

          window.location.href =
            "/datenerhebung";
        }}
        onSettingsClick={() =>
          setShowAccountPanel(
            true
          )
        }
        onExportClick={() =>
          exportToPDF(OVERVIEW_EXPORT_CONFIG, exportDataset)
        }
        accountEmail={
          accountEmail
        }
        isMobile={isMobile}
        highlightAddData={
          hasNoData &&
          !welcomeDismissed
        }
      />

      {!showFeedbackModal && (
        <div
          style={{
            position: "fixed",
            right:
              isCompactFeedback
                ? "1rem"
                : "2rem",
            bottom:
              isCompactFeedback
                ? "1rem"
                : "2rem",
            zIndex: 1500,
            boxShadow:
              "0 16px 40px rgba(15, 23, 42, 0.25)",
            borderRadius:
              "999px",
          }}
        >
          {isCompactFeedback ? (
            <sdx-button
              sr-hint="Feedback öffnen"
              theme="secondary"
              icon-name="icon-message"
              icon-size="2"
              style={{
                minWidth: "0",
              }}
              onClick={() =>
                setShowFeedbackModal(
                  true
                )
              }
            ></sdx-button>
          ) : (
            <sdx-button
              theme="secondary"
              label="Feedback"
              icon-name="icon-message"
              sr-hint="Feedback öffnen"
              onClick={() =>
                setShowFeedbackModal(
                  true
                )
              }
            ></sdx-button>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.75rem"
        }}
      >
        <YearDropdown
          years={years}
          value={selectedYear}
          onChange={setSelectedYear}
          isMobile={isMobile}
        />
        <DataQualityBadge score={dataQualityScore} />
      </div>

      <BalanceSection
        scopeTotals={
          scopeTotals
        }
        currentYearRows={
          yearRows
        }
        isMobile={isMobile}
      />

      <PathwaySection
        yearlyTotals={
          yearlyTotals
        }
        measures={
          recommendedMeasures
        }
        selectedMeasures={
          selectedMeasures
        }
        onToggleMeasure={
          handleToggleMeasure
        }
        onChangeStartYear={
          handleChangeStartYear
        }
        onExportMeasuresChange={
          setExportMeasures
        }
        onUnlockClick={() =>
          setShowPricingModal(
            true
          )
        }
        isMobile={isMobile}
        isCompactInfo={isCompactFeedback}
      />

      <CO2IntensityEnergySection
        co2Intensity={co2Intensity}
        measures={scoringMeasures}
        fleetElectrificationPct={fleetElectrificationPct}
        waterBasePaintPct={waterBasePaintPct}
        repairQuota={repairQuota}
      />

      <NetZeroProgressSection netZero={netZero} />
    </main>
  );
}
